<template>
    <div
        v-if="visible"
        class="fixed bottom-24 left-4 right-4 z-40 mx-auto max-h-[calc(100vh-8rem)] max-w-[calc(100vw-2rem)] overflow-y-auto overflow-x-hidden rounded border border-brand-primary-200 bg-white shadow-lg dark:border-brand-primary-700 dark:bg-neutral-900 md:bottom-6 md:left-auto md:right-6 md:max-w-lg"
    >
        <div class="p-4">
            <div class="mb-3 flex items-start justify-between gap-3">
                <div class="min-w-0">
                    <div class="text-base font-semibold text-neutral-900 dark:text-white">
                        {{ copy.title }}
                    </div>
                    <div v-if="copy.description" class="mt-1 text-sm leading-5 text-neutral-600 dark:text-neutral-300">
                        {{ copy.description }}
                    </div>
                </div>
                <button
                    class="flex h-10 w-10 shrink-0 items-center justify-center rounded text-3xl leading-none text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 dark:hover:bg-neutral-800 dark:hover:text-white"
                    type="button"
                    aria-label="Dismiss idea prompt"
                    @click="handleDismiss"
                >
                    &times;
                </button>
            </div>

            <div v-if="voteIdeas.length" class="mb-4 space-y-2">
                <div class="text-xs font-semibold uppercase text-neutral-500 dark:text-neutral-400">
                    Help shape what comes next
                </div>
                <div
                    class="max-h-[45vh] space-y-2 overflow-y-auto pr-1"
                >
                    <div
                        v-for="idea in voteIdeas"
                        :key="idea.id"
                        class="flex min-w-0 items-start gap-2 rounded bg-neutral-100 p-3 dark:bg-neutral-800 sm:gap-3"
                    >
                        <div class="min-w-0 flex-1">
                            <div class="truncate text-sm font-semibold text-neutral-900 dark:text-white">
                                {{ idea.title }}
                            </div>
                            <div class="mt-1 line-clamp-2 text-xs leading-5 text-neutral-600 dark:text-neutral-300">
                                {{ idea.description }}
                            </div>
                        </div>
                        <div class="flex shrink-0 items-center gap-1">
                            <button
                                class="flex h-8 w-8 items-center justify-center rounded border text-sm"
                                :class="idea.user_vote === 'up'
                                    ? 'border-green-500 bg-green-500 text-white'
                                    : 'border-neutral-300 text-neutral-600 hover:border-green-500 hover:text-green-600 dark:border-neutral-700 dark:text-neutral-300'"
                                type="button"
                                :aria-label="`Thumbs up ${idea.title}`"
                                :disabled="votingIds.includes(idea.id)"
                                @click="handleVote(idea, 'up')"
                            >
                                <Icon icon="thumbs-up" pack="fas" fixed-width />
                            </button>
                            <button
                                class="flex h-8 w-8 items-center justify-center rounded border text-sm"
                                :class="idea.user_vote === 'down'
                                    ? 'border-red-500 bg-red-500 text-white'
                                    : 'border-neutral-300 text-neutral-600 hover:border-red-500 hover:text-red-600 dark:border-neutral-700 dark:text-neutral-300'"
                                type="button"
                                :aria-label="`Thumbs down ${idea.title}`"
                                :disabled="votingIds.includes(idea.id)"
                                @click="handleVote(idea, 'down')"
                            >
                                <Icon icon="thumbs-down" pack="fas" fixed-width />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="error" class="mt-2 text-sm text-red-600 dark:text-red-400">
                {{ error }}
            </div>

            <div class="mt-3 flex flex-wrap items-center justify-end gap-2">
                <button
                    class="rounded bg-brand-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                    type="button"
                    @click="handleShareIdea"
                >
                    Share an Idea
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import {mapActions} from 'vuex';
import Icon from '@/components/Icon.vue';
import {
  capturePostHogEvent,
} from '@/utils/posthog';
import {
  openFeatureIdeaPrompt,
} from '@/utils/feedback.js';

const promptLocation = 'app_shell';

export default {
  name: 'FeatureIdeaPrompt',
  components: {
    Icon,
  },
  data() {
    return {
      copy: {
        title: 'Feature Ideas',
        description: null,
      },
      eligible: false,
      error: null,
      loaded: false,
      manual: false,
      visible: false,
      voteIdeas: [],
      votingIds: [],
    };
  },
  computed: {
    authenticated() {
      return this.$store.state.auth.authenticated;
    },
    user() {
      return this.$store.getters['auth/user'];
    },
  },
  created() {
    this.loadPrompt();
  },
  methods: {
    ...mapActions({
      dismissPrompt: 'featureIdea/dismiss',
      prompt: 'featureIdea/prompt',
      viewedPrompt: 'featureIdea/viewed',
      voteIdea: 'featureIdea/vote',
    }),
    eventProperties(extra = {}) {
      return {
        prompt_location: promptLocation,
        prompt_manual: this.manual,
        user_workout_count: this.user?.workoutCount ?? null,
        ...extra,
      };
    },
    async loadPrompt(manual = false) {
      if ((this.loaded && !manual) || !this.authenticated || !this.user) {
        return;
      }

      this.loaded = true;
      this.manual = manual;

      try {
        const response = await this.prompt({
          params: {
            manual,
          },
        });
        const data = response?.data?.data;

        this.eligible = Boolean(data?.eligible);
        this.copy = {
          ...this.copy,
          ...(data?.copy || {}),
        };
        this.voteIdeas = data?.vote_ideas || [];
        this.visible = this.eligible;

        if (this.visible) {
          capturePostHogEvent('feature_idea_prompt_viewed', this.eventProperties({
            user_completed_workout_count: data?.user_context?.completed_workout_count ?? null,
            vote_idea_count: this.voteIdeas.length,
          }));

          if (this.voteIdeas.length) {
            capturePostHogEvent('feature_idea_vote_prompt_viewed', this.eventProperties({
              vote_idea_ids: this.voteIdeas.map(idea => idea.id),
            }));
          }

          try {
            await this.viewedPrompt({
              manual,
              promptLocation,
              metadata: {
                vote_idea_count: this.voteIdeas.length,
              },
            });
          } catch (error) {
            // PostHog still captures the client-side view; server-side monthly throttle can miss this one view if the network drops.
          }
        }
      } catch (error) {
        this.visible = false;
      }
    },
    handleManualOpen() {
      this.error = null;
      this.loadPrompt(true);
    },
    handleShareIdea() {
      this.visible = false;
      capturePostHogEvent('feature_idea_share_clicked', this.eventProperties({
        vote_idea_count: this.voteIdeas.length,
      }));
      openFeatureIdeaPrompt({
        context: {
          vote_idea_ids: this.voteIdeas.map(idea => idea.id),
        },
        promptLocation,
      });
    },
    async handleDismiss() {
      this.visible = false;
      capturePostHogEvent('feature_idea_prompt_dismissed', this.eventProperties());

      try {
        await this.dismissPrompt({
          promptLocation,
        });
      } catch (error) {
        // Dismiss locally even if the network drops; PostHog still captures production behavior.
      }
    },
    async handleVote(idea, vote) {
      if (this.votingIds.includes(idea.id)) {
        return;
      }

      this.votingIds = [
        ...this.votingIds,
        idea.id,
      ];

      try {
        await this.voteIdea({
          id: idea.id,
          vote,
        });

        this.voteIdeas = this.voteIdeas.map((item) => {
          if (item.id !== idea.id) {
            return item;
          }

          return {
            ...item,
            user_vote: vote,
          };
        });

        capturePostHogEvent('feature_idea_voted', this.eventProperties({
          feature_idea_id: idea.id,
          vote,
        }));
      } catch (error) {
        this.error = 'We could not save that vote. Please try again.';
      } finally {
        this.votingIds = this.votingIds.filter(id => id !== idea.id);
      }
    },
  },
  mounted() {
    window.addEventListener('v3me:feature-idea-prompt', this.handleManualOpen);
  },
  beforeUnmount() {
    window.removeEventListener('v3me:feature-idea-prompt', this.handleManualOpen);
  },
  watch: {
    authenticated() {
      this.loadPrompt();
    },
    user() {
      this.loadPrompt();
    },
  },
};
</script>
